import { CalendarPlus, Zap, ChevronRight, MapPin, Clock, Trophy } from 'lucide-react';
import { getEloLevel, courtDisplayName, getTournamentType } from '../../../engine';
import type {
  ActivityWhen,
  LiveTournament,
  MemberActivity,
  MemberBooking,
  NextItem,
} from '../memberData';
import { greetingName, relativeDayLabel, splitUpcomingPast } from '../memberData';
import { SectionBoundary } from '../SectionBoundary';
import { nextLevelInfo } from '../eloData';
import { Card, LevelChip, SectionTitle, EmptyState, TeamNames } from '../mobileUi';
import { useT } from '../../../i18n';
import type { MobileTab } from '../MobileTabBar';
import { Padel24Logo } from '../../ui/Padel24Logo';

export interface MobileHomeProps {
  member: any;
  myBookings: MemberBooking[];
  myActivities: MemberActivity[];
  next: NextItem | null;
  /** Tournaments in progress the member plays in. */
  live?: LiveTournament[];
  /** The member's next unplayed tournament match (any live tournament, seriespill included). */
  nextMatch?: { live: LiveTournament; match: any } | null;
  effectivePlayerDB?: any[];
  courtMeta?: any;
  onGoTab: (tab: MobileTab) => void;
  /** Spill → Aktiviteter (sign-ups live under the centre page now). */
  onGoActivities?: () => void;
  onGoLive?: () => void;
  /** Kamper → Seriespill. */
  onGoLeague?: () => void;
  /** The level card opens the ELO screen (not Profil). */
  onOpenElo?: () => void;
}

// «Hjem» — greeting, level card, the single next thing, two big actions,
// and a short «kommende» list. Everything a member glances at while walking in.
export function MobileHome({
  member,
  myBookings,
  myActivities,
  next,
  live = [],
  nextMatch = null,
  effectivePlayerDB = [],
  courtMeta,
  onGoTab,
  onGoActivities,
  onGoLive,
  onGoLeague,
  onOpenElo,
}: MobileHomeProps) {
  const t = useT();
  // Full name when it fits the big greeting line, first name otherwise.
  const first = greetingName(member?.name);
  const elo = Math.round(member?.elo || 0);
  const lvl = getEloLevel(elo);
  const nextLvl = nextLevelInfo(elo);
  const hour = new Date().getHours();
  const greet =
    hour < 10
      ? t('mobile.home.greetMorning')
      : hour < 17
        ? t('mobile.home.greetDay')
        : t('mobile.home.greetEvening');

  const { upcoming: upBookings } = splitUpcomingPast(myBookings);
  const { upcoming: upActs } = splitUpcomingPast(myActivities);
  const upcoming = [
    ...upBookings.map((b) => ({ kind: 'booking' as const, startMs: b.startMs, b })),
    ...upActs.map((a) => ({ kind: 'activity' as const, startMs: a.startMs, a })),
  ]
    .sort((x, y) => x.startMs - y.startMs)
    .slice(0, 4);

  return (
    // ── ON A WIDE SCREEN: TWO COLUMNS, SAME SECTIONS ──────────────────
    // (desktop brief, step 2). From the sidebar width the same sections are
    // PLACED, not rewritten: you on the left (greeting, level, the two
    // actions, what is in progress), what is next on the right («Neste for
    // deg», «Kommende»). The markup order is the left column, then the right
    // (#856), so Tab — which follows the markup, not the grid — walks the
    // columns as they are seen; the phone reads the same order top to bottom.
    <div className="px-4 pb-4 space-y-4 @wide:space-y-0 @wide:grid @wide:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] @wide:[grid-template-rows:auto_auto_auto_1fr] @wide:gap-x-8 @wide:gap-y-5 @wide:items-start @wide:pt-2">
      {/* Full wordmark lives here; the app bar keeps only the star to save room.
          From the rail width the rail/sidebar carries the brand instead. */}
      <div className="pt-1 @rail:hidden">
        <Padel24Logo size="md" variant="full" />
      </div>

      {/* Greeting */}
      <div className="@wide:col-start-1 @wide:row-start-1">
        <div className="text-[13px] text-ink-3">{greet},</div>
        <h1 className="font-display font-semibold text-ink leading-tight" style={{ fontSize: 28 }}>
          {first} 👋
        </h1>
      </div>

      {/* Level card.

          NO `aria-label`. It used to carry «Din ELO og trend», and an
          `aria-label` on a content-rich button REPLACES everything inside it:
          the N-level, the ELO number, the level name and the points to the next
          level — the entire reason the card exists — were unreachable, and all a
          screen reader said was the four words of the label. The visible
          content is the accessible name now, and «ELO & trend ›» at the bottom
          of the card already says what tapping it does. */}
      <button
        onClick={() => (onOpenElo ? onOpenElo() : onGoTab('profile'))}
        className="w-full text-left rounded-3xl p-4 relative overflow-hidden active:scale-[0.99] transition-transform @wide:col-start-1 @wide:row-start-2"
        style={{ background: '#0f1611', color: '#fff' }}
      >
        <div
          className="absolute -right-8 -top-8 rounded-full"
          style={{ width: 140, height: 140, background: lvl.color, opacity: 0.35 }}
        />
        <div className="relative flex items-end justify-between">
          <div>
            {/* 0.8, not 0.7 (#1144, 1.4.3): over the level-colour disc the
                card's labels measured 4.12:1 at the worst level; at 0.8 the
                worst is 4.82:1, and they stay a step quieter than the values. */}
            <div className="font-mono text-[10px] uppercase tracking-wider opacity-80">
              {t('mobile.home.yourLevel')}
            </div>
            <div className="font-display font-semibold leading-none mt-1" style={{ fontSize: 34 }}>
              N{lvl.level}
            </div>
            <div className="text-[13px] mt-1 opacity-90">{lvl.name}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-wider opacity-80">ELO</div>
            <div className="font-mono font-bold tabular-nums" style={{ fontSize: 26 }}>
              {elo}
            </div>
          </div>
        </div>
        <div className="relative mt-3">
          <div
            className="rounded-full overflow-hidden"
            style={{ height: 5, background: 'rgba(255,255,255,0.18)' }}
          >
            <div
              style={{
                width: `${Math.round(nextLvl.progress * 100)}%`,
                height: '100%',
                background: '#c2f02f',
              }}
            />
          </div>
          <div className="flex items-center justify-between mt-1.5 text-[11px] opacity-85">
            <span>
              {nextLvl.next
                ? t('mobile.elo.pointsToNext', {
                    points: nextLvl.pointsToNext,
                    level: `N${nextLvl.next.level}`,
                    name: nextLvl.next.name,
                  })
                : t('mobile.home.topLevel')}
            </span>
            <span className="font-mono text-[10px] opacity-70">{t('mobile.home.eloAndTrend')}</span>
          </div>
        </div>
      </button>

      {/* Actions — BEFORE «Pågår» and «Neste for deg» in the markup (#856).
          On a wide screen they sit in the left column under the level card,
          and keyboard focus follows the markup: placed after «Neste for deg»
          (right column) they made Tab jump right, back left, and right again.
          Moving them here makes the focus order the visual order, column by
          column. On the phone they now come straight after the level card.
          Their wide-screen placement is unchanged. */}
      <div className="grid grid-cols-2 gap-3 @wide:col-start-1 @wide:row-start-3">
        <button
          onClick={() => onGoTab('play')}
          className="rounded-3xl p-4 text-left active:scale-[0.98] transition-transform"
          style={{ background: '#c2f02f', color: '#0f1611' }}
        >
          <CalendarPlus size={22} />
          <div className="font-semibold text-[15px] mt-3 leading-tight">
            {t('mobile.home.bookCourt')}
          </div>
          <div className="text-[11px] opacity-70 mt-0.5">{t('mobile.home.bookCourtSub')}</div>
        </button>
        <button
          onClick={() => (onGoActivities ? onGoActivities() : onGoTab('play'))}
          className="rounded-3xl p-4 text-left active:scale-[0.98] transition-transform"
          style={{ background: '#fff', color: '#0f1611', border: '1px solid #ecefe8' }}
        >
          <Zap size={22} />
          <div className="font-semibold text-[15px] mt-3 leading-tight">
            {t('mobile.home.signUp')}
          </div>
          <div className="text-[11px] text-ink-3 mt-0.5">{t('mobile.home.signUpSub')}</div>
        </button>
      </div>

      {/* In progress */}
      {live.length > 0 && (
        <div className="@wide:col-start-1 @wide:row-start-4">
          <SectionTitle>{t('mobile.home.inProgress')}</SectionTitle>
          <div className="space-y-2">
            {/* Each card in its own boundary, and the card reads its record INSIDE
                it: a boundary only catches what its children throw (#1337). */}
            {live.map((l) => (
              <SectionBoundary key={l.key} resetKey={l}>
                <LiveCard l={l} onClick={onGoLive} />
              </SectionBoundary>
            ))}
          </div>
        </div>
      )}

      {/* Next up — the next tournament match first, then the next booking/sign-up */}
      <div className="@wide:col-start-2 @wide:row-start-1 @wide:row-span-2">
        <SectionTitle>{t('mobile.home.nextForYou')}</SectionTitle>
        <div className="space-y-2">
          {nextMatch && (
            <SectionBoundary resetKey={nextMatch}>
              <NextMatchCard
                nextMatch={nextMatch}
                db={effectivePlayerDB}
                meId={member?.id}
                courtMeta={courtMeta}
                onClick={() =>
                  nextMatch.live.tournament?.type === 'seriespill' ? onGoLeague?.() : onGoLive?.()
                }
              />
            </SectionBoundary>
          )}
          {next ? (
            <SectionBoundary resetKey={next.item}>
              <NextCard
                next={next}
                courtMeta={courtMeta}
                onClick={() =>
                  next.kind === 'booking' ? onGoTab('reservations') : onGoActivities?.()
                }
              />
            </SectionBoundary>
          ) : (
            !nextMatch && (
              <EmptyState
                icon={<Clock size={22} />}
                title={t('mobile.home.emptyTitle')}
                hint={t('mobile.home.emptyHint')}
              />
            )
          )}
        </div>
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="@wide:col-start-2 @wide:row-start-3 @wide:row-span-2">
          <SectionTitle
            action={
              <button
                onClick={() => onGoTab('reservations')}
                className="text-[12px] font-semibold text-ink-2 flex items-center"
              >
                {t('mobile.home.seeAll')} <ChevronRight size={14} />
              </button>
            }
          >
            {t('mobile.reservations.upcoming')}
          </SectionTitle>
          <div className="space-y-2">
            {upcoming.map((u, i) =>
              u.kind === 'booking' ? (
                <SectionBoundary key={`b${i}`} resetKey={u.b}>
                  <UpcomingBookingCard mb={u.b} courtMeta={courtMeta} />
                </SectionBoundary>
              ) : (
                <SectionBoundary key={`a${i}`} resetKey={u.a}>
                  <UpcomingActivityCard ma={u.a} />
                </SectionBoundary>
              ),
            )}
          </div>
        </div>
      )}
      <div className="pt-1 text-center @wide:col-start-1 @wide:row-start-5">
        <LevelChip elo={elo} compact />
      </div>
    </div>
  );
}

function LiveCard({ l, onClick }: { l: LiveTournament; onClick?: () => void }) {
  const t = useT();
  const left = l.open.length;
  const tt = getTournamentType(l.tournament.config || l.tournament.type);
  return (
    <Card onClick={onClick} accent={tt.color}>
      <div className="flex items-center gap-3">
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-2xl"
          style={{
            width: 40,
            height: 40,
            background: tt.color + '33',
            color: '#0f1611',
          }}
        >
          <Trophy size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-ink truncate">
            {l.tournament.config?.name || l.tournament.name || t('mobile.common.tournament')}
          </div>
          <div className="text-[11px] text-ink-3">
            {!l.started
              ? t('mobile.home.startingSoon')
              : left > 0
                ? left === 1
                  ? t('mobile.home.matchLeft', { count: left })
                  : t('mobile.home.matchesLeft', { count: left })
                : t('mobile.home.allPlayed')}
          </div>
        </div>
        <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />
      </div>
    </Card>
  );
}

function UpcomingBookingCard({ mb, courtMeta }: { mb: MemberBooking; courtMeta: any }) {
  const t = useT();
  return (
    <Card accent="#0ea5e9">
      <Row
        title={
          mb.plan
            ? t('mobile.reservations.singleMatch')
            : mb.activity?.config?.name || t('mobile.reservations.courtBooking')
        }
        sub={`${relativeDayLabel(mb.booking.date)} · ${mb.booking.startTime}–${mb.booking.endTime}`}
        meta={courtDisplayName(mb.booking.court, courtMeta || {})}
      />
    </Card>
  );
}

function UpcomingActivityCard({ ma }: { ma: MemberActivity }) {
  const t = useT();
  return (
    <Card accent="#84cc16">
      <Row
        title={ma.activity.config?.name || t('mobile.common.activity')}
        sub={activityDayAndTime(ma.when, t)}
        meta={
          ma.registration?.status === 'waitlist'
            ? t('mobile.common.waitlist')
            : t('mobile.common.signedUp')
        }
      />
    </Card>
  );
}

/**
 * «i morgen · 18:00», or «Tid ikke satt» for an activity nothing dates yet —
 * the desktop list's rule since the original app (`fmtWhen`), and the one
 * Reservasjoner already follows (`activityWhenLabel`). Read from `when`, the
 * same sources the card is SORTED by, so a card dated by its booked courts
 * says so instead of reading an empty `config.activityDate` (#1337).
 */
function activityDayAndTime(when: ActivityWhen, t: ReturnType<typeof useT>): string {
  const day = relativeDayLabel(when.date);
  if (!day) return t('mobile.common.timeNotSet');
  return `${day} · ${when.start || ''}`;
}

function Row({ title, sub, meta }: { title: string; sub: string; meta?: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold text-ink truncate">{title}</div>
        <div className="text-[12px] text-ink-3 truncate">{sub}</div>
      </div>
      {meta && (
        <span className="font-mono text-[10px] text-ink-2 flex items-center gap-1 flex-shrink-0">
          <MapPin size={11} /> {meta}
        </span>
      )}
    </div>
  );
}

function NextMatchCard({
  nextMatch,
  db,
  meId,
  courtMeta,
  onClick,
}: {
  nextMatch: { live: LiveTournament; match: any };
  db: any[];
  meId?: any;
  courtMeta: any;
  onClick: () => void;
}) {
  // `tr`, not `t`: this card already binds `t` to the tournament.
  const tr = useT();
  const { live, match: m } = nextMatch;
  const t = live.tournament || {};
  const tt = getTournamentType(t.config || t.type);
  const side = (team: any) => <TeamNames team={team} db={db} meId={meId} />;
  const meta = [
    m.round != null ? tr('mobile.common.round', { round: String(m.round) }) : null,
    m.groupName || m.groupId || null,
    m.court != null ? courtDisplayName(m.court, courtMeta || {}) : null,
    m.startTime || null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Card onClick={onClick} accent={tt.color}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 truncate">
            {tr('mobile.home.nextMatch', { name: t.config?.name || t.name || tt.label })}
            {meta ? ` · ${meta}` : ''}
          </div>
          <div className="font-display font-semibold text-ink truncate" style={{ fontSize: 17 }}>
            {side(m.team1)} <span className="font-mono text-[11px] text-ink-3">vs</span>{' '}
            {side(m.team2)}
          </div>
          <div className="text-[12px] text-ink-2 mt-0.5">
            {live.open.length === 1
              ? tr('mobile.home.matchLeft', { count: live.open.length })
              : tr('mobile.home.matchesLeft', { count: live.open.length })}
          </div>
        </div>
        <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />
      </div>
    </Card>
  );
}

function NextCard({
  next,
  courtMeta,
  onClick,
}: {
  next: NextItem;
  courtMeta: any;
  onClick: () => void;
}) {
  const t = useT();
  if (next.kind === 'booking') {
    const b = next.item.booking;
    const title = next.item.plan
      ? t('mobile.reservations.singleMatch')
      : next.item.activity?.config?.name || t('mobile.reservations.courtBooking');
    return (
      <Card onClick={onClick}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              {relativeDayLabel(b.date)} · {b.startTime}
            </div>
            <div className="font-display font-semibold text-ink truncate" style={{ fontSize: 19 }}>
              {title}
            </div>
            <div className="text-[12px] text-ink-2 flex items-center gap-1 mt-0.5">
              <MapPin size={12} /> {courtDisplayName(b.court, courtMeta || {})} · {b.startTime}–
              {b.endTime}
            </div>
          </div>
          <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />
        </div>
      </Card>
    );
  }
  const a = next.item.activity;
  return (
    <Card onClick={onClick}>
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {activityDayAndTime(next.item.when, t)}
          </div>
          <div className="font-display font-semibold text-ink truncate" style={{ fontSize: 19 }}>
            {a.config?.name || t('mobile.common.activity')}
          </div>
          <div className="text-[12px] text-ink-2 mt-0.5">
            {next.item.registration?.status === 'waitlist'
              ? t('mobile.home.onWaitlist')
              : t('mobile.home.youAreSignedUp')}
          </div>
        </div>
        <ChevronRight size={18} className="text-ink-3 flex-shrink-0" />
      </div>
    </Card>
  );
}
